/**
 * Types for the public backend API.
 *
 * Every shape here is transcribed from web/CONTRACT.md §1 — that file is the
 * source of truth, not this one. If the two disagree, the contract wins and this
 * file is the bug. Nothing is invented: fields the API does not document do not
 * appear here, so a component cannot accidentally depend on one.
 */

export type Source = 'anilist' | 'mal';

/** Human label for a source, for anything user-visible. */
export const SOURCE_LABELS: Record<Source, string> = {
  anilist: 'AniList',
  mal: 'MyAnimeList',
};

// ---------------------------------------------------------------------------
// POST /api/wrapped
// ---------------------------------------------------------------------------

export interface WrappedTitle {
  media_id: number;
  title: string;
  cover_image_url: string | null;
}

/** Every stat except `departure` is null when the list is too thin for it; its slide is skipped. */
export interface WrappedResponse {
  profile: { username: string; source: Source };
  /** How many riders the baselines were measured over. */
  baseline_users: number;
  departure: {
    titles: number;
    hours: number;
    titles_typical: number;
    hours_typical: number;
    /** 0–100: share of riders with fewer titles / hours. */
    titles_percentile: number;
    hours_percentile: number;
  };
  harshness: {
    /** Own mean score minus the community's on the same shows; negative is harsher. */
    delta: number;
    user_mean: number;
    community_mean: number;
    scored: number;
    typical_delta: number;
    /** 0–100: share of riders this one is harsher than. */
    percentile: number;
  } | null;
  obscurity: {
    median_members: number;
    typical_members: number;
    /** 0–100: share of riders whose list is more mainstream. */
    percentile: number;
  } | null;
  hidden_gem: { media_id: number; title: WrappedTitle; user_score: number; members: number } | null;
  disagreement: {
    media_id: number;
    title: WrappedTitle;
    user_score: number;
    community_score: number;
    gap: number;
    members: number;
    top100: boolean;
  } | null;
  home: {
    genre: string;
    share: number;
    typical_share: number;
    percentile: number;
    runners_up: string[];
    /** Every genre on the list, most common first; shares overlap. Older backends omit it. */
    breakdown?: { genre: string; share: number }[];
  } | null;
  drops: {
    rate: number;
    dropped: number;
    typical_rate: number;
    percentile: number;
    quickest: { media_id: number; title: WrappedTitle; episodes: number } | null;
  } | null;
  // The later stops. Optional as well as nullable: a backend from before them omits them.
  score_card?: {
    /** Count of scored titles at each whole score; index 0 is a 1, index 9 a 10. */
    histogram: number[];
    scored: number;
    tens: number;
    /** Share scored 9 or more. */
    top_share: number;
    typical_top_share: number;
    /** 0–100: share of riders who hand out fewer 9s and 10s. */
    percentile: number;
  } | null;
  loved?: {
    media_id: number;
    title: WrappedTitle;
    user_score: number;
    community_score: number;
    gap: number;
    members: number;
  } | null;
  longest?: {
    media_id: number;
    title: WrappedTitle;
    episodes: number;
    hours: number;
    typical_episodes: number;
    percentile: number;
  } | null;
  skipped_genre?: { genre: string; share: number; typical_share: number } | null;
  formats?: {
    mix: { format: 'TV' | 'MOVIE' | 'ONA' | 'OVA' | 'SHORT'; share: number; typical_share: number }[];
    movie_share: number;
    typical_movie_share: number;
    movie_percentile: number;
  } | null;
  fingerprint?: { tags: { tag: string; share: number; lift: number; example: string | null }[] } | null;
  on_board?: {
    current: number;
    paused: number;
    typical: number;
    percentile: number;
    closest: { media_id: number; title: WrappedTitle; progress: number; episodes: number } | null;
  } | null;
  backlog?: { planned: number; hours: number; typical: number; percentile: number } | null;
  era?: {
    median_year: number;
    dated: number;
    oldest: { media_id: number; title: WrappedTitle; year: number };
    newest: { media_id: number; title: WrappedTitle; year: number };
    decades: { decade: number; count: number }[];
  } | null;
  taste_map?: {
    /** In population standard deviations; 0,0 is the middle of the map. */
    x: number;
    y: number;
    side: MapEnd | null;
    ends: Record<MapEnd, string[]>;
    /** x0, x1, y0, y1 of the drawn map. */
    bounds: [number, number, number, number];
    /** Riders per cell: grid[i][j], i along x, j up y. */
    grid: number[][];
    neighbours: number;
    percentile: number;
  } | null;
}

export type MapEnd = 'left' | 'right' | 'up' | 'down';

// ---------------------------------------------------------------------------
// POST /api/recommend
// ---------------------------------------------------------------------------

export interface RecommendRequest {
  /** 1–64 chars, trimmed by the backend; case is preserved for display. */
  username: string;
  source: Source;
  /** 1–50, backend default 10. */
  limit: number;
}

export interface Profile {
  username: string;
  source: Source;
  avatar_url: string | null;
  site_url: string | null;
  /** Total entries on the user's list. */
  list_size: number;
  /** How many of those exist in the model's catalogue. Always <= list_size. */
  matched_items: number;
  /**
   * Set when the site the rider chose could not answer, so this is the account
   * with the same name on the other one. Older backends omit it.
   */
  fallback_from?: Source | null;
  /** Set when this is an old copy of the list, and how old it is. */
  stale_seconds?: number | null;
}

/** Where a request stands in the queue for AniList's budget. */
export type QueueUpdate =
  | { state: 'queued'; position: number; eta_seconds: number }
  | { state: 'working' };

/** An AniList tag on a title. */
export interface MediaTag {
  name: string;
  /** 0–100: how central AniList's voters think the tag is. Always >= 50 here. */
  rank: number;
}

/** An entry from the user's own list that contributed to a recommendation. */
export interface BecauseOf {
  media_id: number;
  title: string;
}

export interface Recommendation {
  /** 1-based, ascending through the array. Rank order is the only calibrated signal. */
  rank: number;
  media_id: number;
  title_romaji: string;
  title_english: string | null;
  /** AniList's native-script title. Null for the handful of entries without one. */
  title_native: string | null;
  cover_image_url: string | null;
  banner_image_url: string | null;
  description: string | null;
  /** AniList's community average, 0–100. Unrelated to `score`. */
  average_score: number | null;
  episodes: number | null;
  /** Runtime of a single episode, not of the whole show. */
  duration_minutes: number | null;
  season_year: number | null;
  /** First studio AniList flags as the main one; null for many older titles. */
  studio: string | null;
  format: string | null;
  genres: string[];
  /** Spoiler-free tags ranked 50+, most central first. May be empty. */
  tags: MediaTag[];
  site_url: string | null;
  /** MyAnimeList's id for the title; null when MAL has none, absent from older backends. */
  mal_id?: number | null;
  /**
   * Raw model score. NOT calibrated and NOT comparable across users or models
   * (CONTRACT.md §1, §2). It must never be rendered as a percentage, a star
   * rating or a "% match". It is typed here only so the field is not silently
   * lost; no component should read it.
   */
  score: number;
  /** Up to 3 items; may be empty (the VAE has no honest decomposition). */
  because_of: BecauseOf[];
}

export interface ModelInfo {
  name: string;
  version: string;
  n_items: number;
}

export interface RecommendResponse {
  profile: Profile;
  /** Ordered by `rank` ascending, length <= the requested limit. */
  recommendations: Recommendation[];
  model: ModelInfo;
  /** Ties feedback events to this answer. Absent or null when the log is off. */
  request_id?: string | null;
}

// ---------------------------------------------------------------------------
// POST /api/events
// ---------------------------------------------------------------------------

export interface FeedbackEvent {
  type: 'view' | 'open' | 'skip';
  media_id: number;
  /** The recommendation's `rank`, which the backend checks against the answer. */
  rank: number;
  /** Station line slug, and 1-based position within that line's tickets. */
  line?: string;
  position?: number;
  /** view only: how long the ticket was on screen. */
  dwell_ms?: number;
}

// ---------------------------------------------------------------------------
// GET /api/health
// ---------------------------------------------------------------------------

export type ModelServiceStatus = 'ok' | 'degraded' | 'unreachable';

export interface HealthResponse {
  status: string;
  model_service: ModelServiceStatus;
  version: string;
}

// ---------------------------------------------------------------------------
// Errors
// ---------------------------------------------------------------------------

/**
 * The documented error codes. Clients key off `code`, never off `message`
 * (CONTRACT.md §1) — `message` is prose and will change.
 */
export const API_ERROR_CODES = [
  'user_not_found',
  'list_private',
  'list_too_small',
  'upstream_error',
  'model_unavailable',
  'rate_limited',
] as const;

export type ApiErrorCode = (typeof API_ERROR_CODES)[number];

/**
 * Codes we synthesise on the client for failures the contract does not cover:
 * the request never reached the backend, or what came back was not the
 * documented envelope. Kept separate from `ApiErrorCode` so it stays obvious
 * which codes the server can actually send.
 */
export type ClientErrorCode = 'network_error' | 'unknown_error';

export type ErrorCode = ApiErrorCode | ClientErrorCode;

export interface ApiErrorEnvelope {
  error: {
    code: string;
    message: string;
  };
}

export function isApiErrorCode(value: string): value is ApiErrorCode {
  return (API_ERROR_CODES as readonly string[]).includes(value);
}
